/**
 * Lectura de comprobantes: de la foto al movimiento.
 *
 * ── LO QUE NUNCA SE HACE ────────────────────────────────────────────────────
 *
 * Nada se guarda automáticamente. La IA propone, tú confirmas. Un monto mal
 * leído que entra solo contamina el histórico y no te enteras hasta que los
 * números no cuadran meses después.
 *
 * ── LA FOTO ─────────────────────────────────────────────────────────────────
 *
 * Se guarda en el dispositivo, NO en la base de datos. Una foto pesa más que
 * mil movimientos juntos; meterla en la base la volvería lenta y el respaldo
 * sería enorme.
 */

import { db, nuevoId } from './db.js';

/** Ancho máximo al que se reduce la foto antes de mandarla. */
const ANCHO_MAXIMO = 1400;

/**
 * Reduce y comprime la foto.
 *
 * Una foto de celular pesa 4 o 5 MB. Mandarla entera por datos móviles en
 * Montería es lento y caro, y el modelo no lee mejor por eso. A 1400 px de
 * ancho el texto de una factura sigue siendo legible y pesa ~15 veces menos.
 *
 * Se devuelven DOS versiones: la reducida que se manda a la IA, y una copia
 * un poco más grande que se guarda como respaldo visual.
 */
export async function prepararImagen(archivo) {
  const bitmap = await createImageBitmap(archivo);

  const escala = Math.min(1, ANCHO_MAXIMO / Math.max(bitmap.width, bitmap.height));
  const ancho = Math.round(bitmap.width * escala);
  const alto = Math.round(bitmap.height * escala);

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto;
  lienzo.getContext('2d').drawImage(bitmap, 0, 0, ancho, alto);
  bitmap.close?.();

  const blob = await new Promise((r) => lienzo.toBlob(r, 'image/jpeg', 0.82));
  const base64 = await blobABase64(blob);

  return {
    blob,
    base64,
    mime: 'image/jpeg',
    ancho,
    alto,
    kb: Math.round(blob.size / 1024),
  };
}

function blobABase64(blob) {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    // El resultado viene como "data:image/jpeg;base64,XXXX" y la API solo
    // quiere la parte de después de la coma.
    lector.onload = () => resolver(String(lector.result).split(',')[1]);
    lector.onerror = rechazar;
    lector.readAsDataURL(blob);
  });
}

/** Guarda la foto en el dispositivo y devuelve su identificador. */
export async function guardarFoto(blob, movimientoId = null) {
  const id = nuevoId();
  await db.adjuntos.add({
    id,
    movimiento_id: movimientoId,
    blob,
    tipo: blob.type,
    tamano: blob.size,
    creado_en: Date.now(),
  });
  return id;
}

export async function obtenerFoto(id) {
  const a = await db.adjuntos.get(id);
  return a ? URL.createObjectURL(a.blob) : null;
}

/**
 * Revisa lo que devolvió la IA antes de dejarlo pasar.
 *
 * El contenido de una foto no es confiable: puede traer texto puesto para
 * engañar al modelo. El prompt ya defiende contra eso, pero una sola capa no
 * basta cuando se trata de plata. Aquí se valida que los números tengan
 * sentido, del lado del cliente, donde el atacante no llega.
 */
export function revisar(extraido) {
  const problemas = [];
  const avisos = [];

  if (extraido?.alerta_seguridad) {
    problemas.push({
      grave: true,
      texto: 'Esta imagen contiene texto que intenta manipular la lectura. ' +
             'Revisa los datos con cuidado antes de guardar.',
      detalle: extraido.alerta_seguridad,
    });
  }

  const monto = Number(extraido?.monto);
  if (!monto || monto <= 0) {
    avisos.push('No se pudo leer el monto. Escríbelo a mano.');
  } else if (monto > 50_000_000) {
    // No es imposible, pero a esa cifra vale la pena mirar dos veces.
    avisos.push('El monto es muy alto. Confirma que esté bien leído.');
  }

  // Si la fecha viene del futuro, algo se leyó mal.
  if (extraido?.fecha) {
    const f = new Date(extraido.fecha + 'T12:00:00');
    if (Number.isNaN(f.getTime())) {
      avisos.push('La fecha no se entendió. Escógela a mano.');
    } else if (f > new Date(Date.now() + 86400000)) {
      avisos.push('La fecha leída está en el futuro. Revísala.');
    }
  }

  // Los campos que el modelo mismo marcó como dudosos.
  const conf = extraido?.confianza ?? {};
  const dudosos = Object.entries(conf)
    .filter(([, v]) => typeof v === 'number' && v < 0.7)
    .map(([k]) => k);

  // Si los productos no suman lo que dice el total, no se corrige: se avisa.
  // Puede ser un descuento legítimo o un renglón mal leído, y solo el dueño
  // de la factura sabe cuál de las dos.
  const productos = extraido?.productos ?? [];
  if (productos.length) {
    const suma = productos.reduce((t, p) => t + (Number(p.valor_total) || 0), 0);
    const base = Number(extraido?.subtotal) || monto;
    if (base && Math.abs(suma - base) > Math.max(base * 0.02, 500)) {
      avisos.push(
        `Los productos suman ${suma.toLocaleString('es-CO')} pero el documento ` +
        `dice ${base.toLocaleString('es-CO')}. Puede ser un descuento, o un renglón mal leído.`
      );
    }
  }

  return { problemas, avisos, dudosos, faltantes: extraido?.campos_faltantes ?? [] };
}

/** Convierte lo extraído en los campos que espera el formulario. */
export function aBorrador(extraido) {
  return {
    monto: Math.round(Number(extraido?.monto) || 0),
    fecha: extraido?.fecha || null,
    hora: extraido?.hora || null,
    concepto: extraido?.concepto || extraido?.establecimiento || '',
    contraparte: extraido?.establecimiento || extraido?.contraparte_nombre || '',
    referencia: extraido?.referencia || '',
    impuestos: Math.round(Number(extraido?.impuestos) || 0),
    propina: Math.round(Number(extraido?.propina) || 0),
    tipo: extraido?.direccion === 'ingreso' ? 'ingreso'
        : extraido?.direccion === 'transferencia_propia' ? 'transferencia'
        : 'egreso',
    productos: (extraido?.productos ?? []).map((p) => ({
      descripcion: p.descripcion ?? '',
      cantidad: Number(p.cantidad) || 1,
      valor_total: Math.round(Number(p.valor_total) || 0),
      categoria_sugerida: p.categoria_sugerida ?? null,
    })),
  };
}
